import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AppliedFilter,
  FilterType,
  MultipleDropdownFilter,
} from '@shared/components/filters/Domain/filter.model';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgForOf, NgIf, NgSwitchCase } from '@angular/common';
import { PRIMENGModule } from '@shared/prime-ng/prime-ng.module';
import { TranslateModule } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { FilterService } from '@shared/components/filters/Application/Services/filter.service';
import { FilterStorageService } from '@shared/components/filters/Application/Services/filter-storage.service';

@Component({
  selector: 'app-multiple-filters',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgForOf,
    PRIMENGModule,
    TranslateModule,
    NgIf,
    DividerModule,
    NgSwitchCase,
  ],
  templateUrl: './multiple-filters.component.html',
  styleUrl: './multiple-filters.component.scss',
})
export class MultipleFiltersComponent implements OnInit {
  @Input() filter!: AppliedFilter;
  @Output() filterChange = new EventEmitter<AppliedFilter>();

  //TODO: Change value of appliedFilter as {label: string; value: any}[]
  filterForm!: FormGroup;
  multiDropdowns!: MultipleDropdownFilter;
  isFirstLoad = true;
  constructor(
    private fb: FormBuilder,
    private filterService: FilterService,
    private filterStorage: FilterStorageService,
  ) {
    this.filterForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.multiDropdowns = this.filter.filter as MultipleDropdownFilter;

    if (this.isFirstLoad)
      //SET CONTROLS AND SET VALUE FROM PARENT
      this.multiDropdowns.dropdowns.forEach((dropdown, index) => {
        const controlValue =
          this.filter.value?.[`dropdown-${index}`] ??
          (dropdown.type === FilterType.Multiselect || dropdown.type === FilterType.TreeSelect
            ? []
            : null);
        const validators = dropdown.type === FilterType.Multiselect ? [Validators.required] : [];

        this.filterForm.addControl(`dropdown-${index}`, this.fb.control(controlValue, validators));
      });

    this.isFirstLoad = false;

    //Patch values from service on second load
    this.filterForm.patchValue(this.filterStorage.load('composedFilter') || {});

    //Emit values on change and also set values on the service
    this.filterForm.valueChanges.subscribe(() => {
      if (this.filterForm.valid) {
        this.filterStorage.saveComposedFilter('composedFilter', this.filterForm.value);
        this.emitUpdatedFilter();
      }
    });
  }

  // Emit updated applied filter
  emitUpdatedFilter() {
    const allDropdownsFilled = this.multiDropdowns.dropdowns.every(
      (dropdown, index) => this.filterForm.get(`dropdown-${index}`)?.value?.length > 0,
    );

    if (allDropdownsFilled) {
      const filteredValues: { dropdown: string; label: string; value: any }[] = [];

      Object.entries(this.filterForm.value).forEach(([key, value]) => {
        const dropdownIndex = parseInt(key.split('-')[1], 10);
        const dropdown = this.multiDropdowns.dropdowns[dropdownIndex];

        switch (dropdown.type) {
          case FilterType.Dropdown:
            if (value) {
              const option = this.filterService.getOptionLabelFromValue(dropdown, value);
              filteredValues.push({
                dropdown: key,
                label: option.label,
                value: option.value,
              });
            }
            break;
          case FilterType.Multiselect:
            if (Array.isArray(value)) {
              value.forEach((item) => {
                filteredValues.push({
                  dropdown: key,
                  label: item.label,
                  value: item.value,
                });
              });
            }
            break;
          case FilterType.TreeSelect:
            if (Array.isArray(value)) {
              value.forEach((item) => {
                filteredValues.push({
                  dropdown: key,
                  label: item.label,
                  value: item.key,
                });
              });
            }
            break;
          default:
            console.warn('Unknown type:');
        }
      });

      const appliedFilter: AppliedFilter = {
        ...this.filter,
        isApplied: allDropdownsFilled,
        value: filteredValues,
      };

      this.filterChange.emit(appliedFilter);
    } else {
      const appliedFilter: AppliedFilter = {
        ...this.filter,
        isApplied: allDropdownsFilled,
        value: {},
      };
      this.filterChange.emit(appliedFilter);
    }
  }

  protected readonly FilterType = FilterType;
}
